import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  ShieldCheck, Loader2, FileSearch, Download, CheckCircle2, XCircle, Clock,
  AlertTriangle, Landmark, Building2, ScrollText, Package,
} from "lucide-react";
import { API } from "@/lib/api";

const CAT_ICON = {
  Ipotecaria: Landmark,
  Catasto: ScrollText,
  Camerale: Building2,
};

const MANUAL_FIELDS = ["cf_piva", "conservatoria", "denominazione", "sezione", "sezione_urbana"];

function cleanMsg(msg) {
  if (!msg) return "";
  const m = String(msg);
  const match = m.match(/<Message>([^<]+)<\/Message>/i) || m.match(/<Code>([^<]+)<\/Code>/i);
  const out = match ? match[1] : m.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  return out.length > 140 ? out.slice(0, 140) + "…" : out;
}

const STATO_BADGE = {
  completata: { c: "#10B981", icon: CheckCircle2, label: "Completata" },
  in_corso: { c: "#EA7B2B", icon: Clock, label: "In corso" },
  errore: { c: "#E11D48", icon: XCircle, label: "Errore" },
};

export function DueDiligencePanel({ dealId, isDemo }) {
  const [catalog, setCatalog] = useState(null);
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [quote, setQuote] = useState(null);
  const [extra, setExtra] = useState({});
  const [loadingQuote, setLoadingQuote] = useState("");
  const [executing, setExecuting] = useState(false);
  const [pkgMode, setPkgMode] = useState(false);

  const loadRequests = useCallback(() => {
    api.get(`/deals/${dealId}/due-diligence`).then((r) => setRequests(r.data)).catch(() => {});
  }, [dealId]);

  useEffect(() => {
    api.get("/due-diligence/catalog").then((r) => setCatalog(r.data)).catch(() => {});
    loadRequests();
  }, [dealId, loadRequests]);

  const openService = async (serviceKey) => {
    setLoadingQuote(serviceKey);
    try {
      const { data } = await api.post(`/deals/${dealId}/due-diligence/quote`, { service_key: serviceKey, extra: {} });
      const init = {};
      data.dati_richiesti.forEach((f) => { if (f.valore != null) init[f.campo] = f.valore; });
      if (data.categoria === "Camerale") init.tipo_societa = "capitale";
      setExtra(init);
      setQuote(data);
      setPkgMode(false);
      setOpen(true);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoadingQuote("");
    }
  };

  const openPackage = () => {
    setQuote(null);
    setPkgMode(true);
    setExtra({});
    setOpen(true);
  };

  const runExecute = async (serviceKey, extraData) => {
    const { data } = await api.post(`/deals/${dealId}/due-diligence/execute`, {
      service_key: serviceKey, extra: extraData, confirm: true,
    });
    return data;
  };

  const confirmSingle = async () => {
    setExecuting(true);
    try {
      const res = await runExecute(quote.service_key, extra);
      if (res.stato === "completata") toast.success(`${quote.label}: completata`);
      else toast.warning(`${quote.label}: ${cleanMsg(res.errore) || "errore"}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 402 && detail?.ricarica_necessaria) {
        toast.error(`Saldo insufficiente (servono € ${detail.richiesto?.toFixed(2)})`, {
          description: `Disponibili € ${detail.disponibile?.toFixed(2)}. Ricarica il Wallet per procedere.`,
          action: { label: "Vai al Wallet", onClick: () => { window.location.href = "/wallet"; } },
        });
      } else if (e.response?.status === 403 && detail?.limite_superato) {
        toast.error("Limite di spesa mensile superato", {
          description: `Speso € ${detail.speso?.toFixed(2)} + € ${detail.richiesto?.toFixed(2)} supera il limite di € ${detail.limite?.toFixed(2)}. Contatta l'amministratore.`,
        });
      } else {
        toast.error(cleanMsg(apiError(detail)));
      }
    } finally {
      setOpen(false);
      setExecuting(false);
      loadRequests();
    }
  };

  const confirmPackage = async () => {
    setExecuting(true);
    const keys = catalog?.pacchetto?.services || [];
    let ok = 0;
    try {
      for (const k of keys) {
        try {
          const res = await runExecute(k, {});
          if (res.stato === "completata") ok += 1;
        } catch { /* continua */ }
      }
      toast[ok === keys.length ? "success" : "warning"](`Due Diligence completa: ${ok}/${keys.length} servizi completati`);
    } finally {
      setExecuting(false);
      setOpen(false);
      loadRequests();
    }
  };

  const setField = (k, v) => setExtra((e) => ({ ...e, [k]: v }));

  const editableFields = quote
    ? quote.dati_richiesti.filter((f) => MANUAL_FIELDS.includes(f.campo) || !f.presente)
    : [];
  const readyToConfirm = quote
    ? quote.dati_richiesti.filter((f) => f.obbligatorio).every((f) => (extra[f.campo] ?? f.valore) != null && (extra[f.campo] ?? f.valore) !== "")
    : false;

  if (!catalog) return null;

  const byCat = {};
  catalog.servizi.forEach((s) => { (byCat[s.categoria] = byCat[s.categoria] || []).push(s); });
  const order = ["Ipotecaria", "Catasto", "Camerale"];

  return (
    <div className="bg-[#122A3F] border border-white/10 rounded-sm" data-testid="section-duediligence">
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
        <h2 className="text-sm font-medium text-zinc-200 uppercase tracking-wider flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#EA7B2B]" /> Due Diligence
        </h2>
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/30">
          {catalog.ambiente === "sandbox" ? "SANDBOX" : "PRODUZIONE"} · on-demand
        </span>
      </div>
      <div className="p-5 space-y-4">
        {isDemo && (
          <div data-testid="dd-demo-notice" className="flex items-center gap-2 text-xs text-[#EA7B2B] bg-[#EA7B2B]/10 border border-[#EA7B2B]/30 rounded-sm px-3 py-2">
            <ShieldCheck className="w-4 h-4 shrink-0" />
            Operazione DEMO: Due Diligence reale e addebiti Wallet disabilitati in ambiente di test.
          </div>
        )}
        {/* Pacchetto completo */}
        <button
          onClick={openPackage}
          data-testid="dd-package-btn"
          className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-sm bg-[#EA7B2B]/10 border border-[#EA7B2B]/40 hover:bg-[#EA7B2B]/20 transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            <Package className="w-5 h-5 text-[#EA7B2B]" />
            <div>
              <div className="text-sm font-medium text-white">{catalog.pacchetto.label}</div>
              <div className="text-[11px] text-zinc-400">{catalog.pacchetto.descrizione}</div>
            </div>
          </div>
          <div className="font-mono text-sm text-[#EA7B2B] whitespace-nowrap">€ {catalog.pacchetto.costo.toFixed(2)}</div>
        </button>

        {/* Servizi per categoria */}
        {order.filter((c) => byCat[c]).map((cat) => {
          const Icon = CAT_ICON[cat] || FileSearch;
          return (
            <div key={cat}>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
                <Icon className="w-3.5 h-3.5" /> {cat}
              </div>
              <div className="space-y-1.5">
                {byCat[cat].map((s) => {
                  const alta = s.priorita === "alta";
                  return (
                    <div
                      key={s.key}
                      data-testid={`dd-service-${s.key}`}
                      className={`flex items-center justify-between gap-2 px-3 py-2 rounded-sm border ${alta ? "border-[#E11D48]/40 bg-[#E11D48]/5" : "border-white/10 bg-[#0A1826]"}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm text-white flex items-center gap-2">
                          {s.label}
                          {alta && <span className="text-[9px] uppercase px-1.5 py-0.5 rounded-sm bg-[#E11D48]/20 text-[#F87171]">priorità alta</span>}
                        </div>
                        <div className="text-[11px] text-zinc-500 truncate">{s.tempo_stimato} · {s.dati_richiesti.join(", ")}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-mono text-xs text-zinc-400">€ {(s.prezzo ?? s.costo).toFixed(2)}</span>
                        <button
                          onClick={() => openService(s.key)}
                          disabled={loadingQuote === s.key}
                          data-testid={`dd-request-${s.key}`}
                          className="text-xs px-3 py-1.5 rounded-sm bg-[#EA7B2B] text-black font-medium hover:bg-[#F59E4A] transition-colors disabled:opacity-50"
                        >
                          {loadingQuote === s.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Richiedi"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Richieste effettuate */}
        {requests.length > 0 && (
          <div className="pt-3 border-t border-white/10">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Richieste effettuate</div>
            <div className="space-y-1.5" data-testid="dd-requests-list">
              {requests.map((r) => {
                const b = STATO_BADGE[r.stato] || STATO_BADGE.errore;
                const Icon = b.icon;
                return (
                  <div key={r.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-sm bg-[#0A1826] border border-white/10" data-testid={`dd-req-${r.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">{r.label}</div>
                      <div className="text-[11px] text-zinc-500">{new Date(r.data_richiesta).toLocaleString("it-IT")} · {r.user_name} · € {r.costo?.toFixed(2)}</div>
                      {r.stato === "errore" && r.errore && <div className="text-[11px] text-[#F87171] truncate">{cleanMsg(r.errore)}</div>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: b.c }}>
                        <Icon className="w-3.5 h-3.5" /> {b.label}
                      </span>
                      {r.documento?.path && (
                        <a
                          href={`${API}/due-diligence/requests/${r.id}/document`}
                          target="_blank" rel="noreferrer"
                          className="text-zinc-400 hover:text-[#EA7B2B]"
                          data-testid={`dd-download-${r.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Dialog di conferma */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#122A3F] border-white/10 text-white max-w-lg" data-testid="dd-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">
              {pkgMode ? catalog.pacchetto.label : quote?.label}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {pkgMode ? "Verranno eseguiti i servizi del pacchetto in sequenza." : quote?.descrizione}
            </DialogDescription>
          </DialogHeader>

          {pkgMode ? (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-zinc-400">Servizi inclusi</span><span>{catalog.pacchetto.services.length}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Costo stimato totale</span><span className="font-mono text-[#EA7B2B]">€ {catalog.pacchetto.costo.toFixed(2)}</span></div>
              <ul className="text-[13px] text-zinc-300 list-disc pl-5 space-y-1">
                {catalog.pacchetto.services.map((k) => {
                  const s = catalog.servizi.find((x) => x.key === k);
                  return <li key={k}>{s?.label} — € {(s?.prezzo ?? s?.costo).toFixed(2)}</li>;
                })}
              </ul>
            </div>
          ) : quote && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <Info label="Costo stimato" value={`€ ${quote.costo.toFixed(2)}`} accent />
                <Info label="Tempo previsto" value={quote.tempo_stimato} />
                <Info label="Fonte" value={quote.fonte} />
                <Info label="Ambiente" value={quote.ambiente === "sandbox" ? "Sandbox" : "Produzione"} />
              </div>

              {/* Dati necessari (editabili) */}
              <div>
                <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Dati necessari</div>
                <div className="space-y-2">
                  {editableFields.length === 0 && (
                    <div className="text-[13px] text-[#10B981] flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Tutti i dati necessari sono già disponibili dal deal.</div>
                  )}
                  {editableFields.map((f) => (
                    <div key={f.campo} className="flex items-center gap-2">
                      <label className="text-[12px] text-zinc-400 w-32 shrink-0">{f.label}{f.obbligatorio && <span className="text-[#F87171]"> *</span>}</label>
                      <input
                        value={extra[f.campo] ?? ""}
                        onChange={(e) => setField(f.campo, e.target.value)}
                        placeholder={f.obbligatorio ? "obbligatorio" : "opzionale"}
                        data-testid={`dd-field-${f.campo}`}
                        className="flex-1 bg-[#0A1826] border border-white/10 rounded-sm px-2 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#EA7B2B]"
                      />
                    </div>
                  ))}
                  {quote.categoria === "Camerale" && (
                    <div className="flex items-center gap-2">
                      <label className="text-[12px] text-zinc-400 w-32 shrink-0">Tipo società</label>
                      <select
                        value={extra.tipo_societa || "capitale"}
                        onChange={(e) => setField("tipo_societa", e.target.value)}
                        data-testid="dd-field-tipo_societa"
                        className="flex-1 bg-[#0A1826] border border-white/10 rounded-sm px-2 py-1.5 text-sm text-white focus:outline-none"
                      >
                        <option value="capitale">Società di capitale</option>
                        <option value="persone">Società di persone</option>
                        <option value="individuale">Impresa individuale</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {quote.limitazioni && (
                <div className="flex items-start gap-2 text-[12px] text-zinc-400 bg-[#0A1826] border border-white/10 rounded-sm p-2.5">
                  <AlertTriangle className="w-4 h-4 text-[#F59E0B] shrink-0 mt-0.5" />
                  <span>{quote.limitazioni}</span>
                </div>
              )}
              <div className="text-[11px] text-zinc-500">{quote.note}</div>
            </div>
          )}

          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm text-zinc-300 hover:text-white" data-testid="dd-cancel-btn">Annulla</button>
            <button
              onClick={pkgMode ? confirmPackage : confirmSingle}
              disabled={executing || (!pkgMode && !readyToConfirm)}
              data-testid="dd-confirm-btn"
              className="flex items-center gap-2 px-4 py-2 bg-[#EA7B2B] text-black text-sm font-medium rounded-sm hover:bg-[#F59E4A] transition-colors disabled:opacity-50"
            >
              {executing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              {pkgMode ? `Conferma ed esegui (€ ${catalog.pacchetto.costo.toFixed(2)})` : `Conferma (€ ${quote?.costo.toFixed(2)})`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value, accent }) {
  return (
    <div className="bg-[#0A1826] border border-white/10 rounded-sm px-3 py-2">
      <div className="text-[10px] text-zinc-500 uppercase">{label}</div>
      <div className={`text-sm ${accent ? "font-mono text-[#EA7B2B]" : "text-white"}`}>{value}</div>
    </div>
  );
}
